import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type {
  BulkImportBatchSummary,
  ListBulkImportBatchesParams,
  ListBulkImportBatchesResult,
} from "../types";

interface UseBulkImportHistoryResult {
  batches: readonly BulkImportBatchSummary[];
  nextCursor: string | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useBulkImportHistory(
  params: ListBulkImportBatchesParams = {},
): UseBulkImportHistoryResult {
  const [batches, setBatches] = useState<readonly BulkImportBatchSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams();
        if (params.importType) queryParams.set("importType", params.importType);
        if (params.status) queryParams.set("status", params.status);
        if (params.limit !== undefined) {
          queryParams.set("limit", String(params.limit));
        }
        if (params.cursor) queryParams.set("cursor", params.cursor);

        const queryString = queryParams.toString();
        const url = `/api/bulk-imports${queryString ? `?${queryString}` : ""}`;

        const data = await apiFetch.get<ListBulkImportBatchesResult>(url);
        setBatches(data.batches);
        setNextCursor(data.nextCursor);
      } catch (err) {
        setError(toError(err));
        setBatches([]);
        setNextCursor(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [fetchKey, params.importType, params.status, params.limit, params.cursor]);

  const refetch = useCallback(() => setFetchKey((key) => key + 1), []);

  return {
    batches,
    nextCursor,
    isLoading,
    error,
    refetch,
  };
}

function toError(error: unknown): Error {
  if (error instanceof ApiError) {
    return error;
  }

  return error instanceof Error ? error : new Error("Unknown error");
}
